import { type DisplayContact } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { AnimatedLegendList } from '@legendapp/list/reanimated'
import { useMemo } from 'react'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import { useSpinDelay } from 'spin-delay'
import { useDebounce } from 'use-debounce'
import { ContactListItem } from '~/components/contacts/ContactListItem'
import Icon from '~/components/Icon'
import { FilterBar, SortBar } from '~/components/SortAndFilter'
import { type SortDirection } from '~/components/SortAndFilter'
import { Heading1 } from '~/components/typography/Titles'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { useContactsPaginated, type ContactsListSortBy } from '~/data/contacts/useContacts'
import { cn } from '~/lib/utils'
import { Skeleton } from '../ui/skeleton'

type FilterKey = 'hasRespondedFeedback' | 'hasPendingFeedback' | 'hasNoFeedback'

export function ManageUserContacts() {
  const [searchInput, setSearchInput] = useState('')
  const [sortBy, setSortBy] = useState<ContactsListSortBy>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set())

  const handleFilterToggle = useCallback((key: FilterKey) => {
    setActiveFilters((current) => {
      const next = new Set(current)

      if (next.has(key)) {
        next.delete(key)
      } else {
        const feedbackFilters: FilterKey[] = [
          'hasRespondedFeedback',
          'hasPendingFeedback',
          'hasNoFeedback',
        ]

        if (feedbackFilters.includes(key)) {
          for (const feedbackFilter of feedbackFilters) {
            next.delete(feedbackFilter)
          }
        }

        next.add(key)
      }

      return next
    })
  }, [])

  const [debouncedSearchTerm] = useDebounce(searchInput, 300)

  const { contacts, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching } =
    useContactsPaginated({
      sortBy,
      sortDirection,
      filters: activeFilters,
      search: debouncedSearchTerm,
    })

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const renderItem = useCallback(
    ({ item }: { item: DisplayContact }) => (
      <ContactListItem
        contact={item}
        linkToContact
        showFeedbackStatus
        className="mx-0 my-0.5 mb-4"
      />
    ),
    []
  )

  const keyExtractor = useCallback((item: DisplayContact) => item.id, [])
  const isLoadingDebounced = useSpinDelay(isLoading || isFetching, {
    delay: 100,
    minDuration: 400,
  })

  return (
    <View className="flex-1">
      <ContactsListHeader
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={setSortBy}
        onSortDirectionChange={setSortDirection}
        activeFilters={activeFilters}
        onFilterToggle={handleFilterToggle}
        className="px-5 pt-6"
      />
      {isLoadingDebounced && contacts.length === 0 && <ContactItemSkeleton className="mb-20" />}
      {!isLoadingDebounced && contacts.length === 0 && <ContactsEmptyState />}
      {!isLoadingDebounced && contacts.length > 0 && (
        <AnimatedLegendList
          data={contacts}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          showsVerticalScrollIndicator={false}
          className={cn(
            'flex-1',
            isLoadingDebounced ? 'opacity-0 transition-opacity duration-150' : 'opacity-100'
          )}
          contentContainerClassName="pb-18 px-5 pt-4"
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        />
      )}
    </View>
  )
}

function ContactsListHeader({
  searchInput,
  onSearchChange,
  sortBy,
  sortDirection,
  onSortChange,
  onSortDirectionChange,
  activeFilters,
  onFilterToggle,
  className,
}: {
  searchInput: string
  onSearchChange: (value: string) => void
  sortBy: ContactsListSortBy
  sortDirection: SortDirection
  onSortChange: (sort: ContactsListSortBy) => void
  onSortDirectionChange: (direction: SortDirection) => void
  activeFilters: Set<FilterKey>
  onFilterToggle: (key: FilterKey) => void
  className?: string
}) {
  const { t } = useTranslations('contacts')

  const sortOptions = useMemo(
    () => [
      { key: 'created_at' as const, label: t(($) => $.list.sortBy.createdAt) },
      { key: 'name' as const, label: t(($) => $.list.sortBy.name) },
      { key: 'readiness' as const, label: t(($) => $.list.sortBy.readiness) },
    ],
    [t]
  )

  const filterOptions = useMemo(
    () => [
      { key: 'hasRespondedFeedback' as const, label: t(($) => $.list.filter.hasRespondedFeedback) },
      { key: 'hasPendingFeedback' as const, label: t(($) => $.list.filter.hasPendingFeedback) },
      { key: 'hasNoFeedback' as const, label: t(($) => $.list.filter.hasNoFeedback) },
    ],
    [t]
  )

  return (
    <View className={cn('gap-4', className)}>
      <View className="flex-row items-center justify-center gap-3">
        <Heading1 className="text-center font-light">{t(($) => $.list.title)}</Heading1>
      </View>

      <Input
        value={searchInput}
        onChangeText={onSearchChange}
        placeholder={t(($) => $.list.search)}
        variant="box"
        showClearButton
        className="bg-background"
        inputClassName="text-base"
      />

      <SortBar
        sortOptions={sortOptions}
        currentSort={sortBy}
        onSortChange={onSortChange}
        sortDirection={sortDirection}
        onSortDirectionChange={onSortDirectionChange}
      />

      <FilterBar
        activeFilters={activeFilters}
        onFilterToggle={onFilterToggle}
        className="-mx-5 border-y border-tertiary-foreground px-5 py-3"
        filterOptions={filterOptions}
      />
    </View>
  )
}

function ContactsEmptyState() {
  const { t } = useTranslations('contacts')

  return (
    <View className="flex-1 items-center justify-center gap-4 px-5 py-16 pb-32">
      <Icon name="account-multiple-remove-outline" size={48} className="text-secondary/60" />
      <View className="items-center gap-2">
        <Text className="font-medium text-base text-primary">{t(($) => $.list.empty)}</Text>
        <Text className="text-center text-sm text-secondary">
          {t(($) => $.list.emptyDescription)}
        </Text>
      </View>
    </View>
  )
}

function ContactItemSkeleton({ className }: { className?: string }) {
  return (
    <View
      className={cn(
        'native:shadow-card mx-5 mt-4 items-start gap-2 rounded-lg bg-card p-2 py-4 pr-4 shadow-md shadow-primary/20',
        className
      )}>
      <View className="w-full flex-row gap-2">
        <View className="flex-1 basis-2/3 gap-2">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-8 w-full rounded-lg" />
        </View>
        <View className="flex-1 basis-1/3 items-start justify-start gap-x-6 gap-y-3">
          <Skeleton className="h-8 w-full rounded-lg" />
        </View>
      </View>
      <View className="w-full flex-row gap-2">
        <Skeleton className="h-12 w-32 rounded-lg" />
        <Skeleton className="ml-auto size-12 rounded-lg" />
      </View>
    </View>
  )
}
